// SideBySideViewer — side-by-side document comparison with highlighted
// differences (docs-diff-viewer).
//
// Shows the original document on the left, the revision on the right.
// Each side displays the extracted text (with --- Page N --- markers) in a
// scrollable pane. Both panes iterate over the SAME aligned diff-line
// sequence so rows line up 1:1. A line that only exists on one side
// renders as a colored SPACER on the opposite side:
//   - Added text:   green background on the revision side + green spacer
//                   on the original side (where it was inserted)
//   - Removed text: red background + strikethrough on the original side +
//                   red spacer on the revision side (where it is missing)
//   - Changed text: yellow background on both sides (the orig half on the
//                   left, the rev half on the right; each opposite half
//                   renders as a yellow spacer so the pair stays aligned)
//
// A simple difflib-style line-diff is computed client-side from the two
// texts. The parent can call scrollToPage(page) or
// scrollToDifferenceBlock(blockIndex) via the ref API to scroll both panes
// to a specific location.

import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface SideBySideViewerHandle {
  /** Scroll both panes to the given page marker (1-indexed). */
  scrollToPage: (page: number) => void;
  /** Scroll both panes to the Nth non-equal block (0-indexed) in the diff. */
  scrollToDifferenceBlock: (blockIndex: number) => void;
}

interface SideBySideViewerProps {
  originalFilename: string;
  revisionFilename: string;
  originalText: string;
  revisionText: string;
}

// ---- Line-level diff (LCS / SequenceMatcher-style) ----

type DiffTag = 'equal' | 'added' | 'removed' | 'changed';

interface DiffLine {
  text: string;
  tag: DiffTag;
  origLineNum: number; // 0-based, -1 if N/A
  revLineNum: number;  // 0-based, -1 if N/A
  page: number;        // approximate page (from preceding --- Page N --- marker)
}

/**
 * Compute a line-level diff between two texts. Uses a simple
 * longest-common-subsequence approach (equivalent to difflib's
 * SequenceMatcher at the line level). Returns an array of DiffLine
 * objects with tags: equal | added | removed | changed.
 */
function computeLineDiff(
  original: string,
  revision: string,
): DiffLine[] {
  const origLines = original.split('\n');
  const revLines = revision.split('\n');

  // Build LCS table.
  const m = origLines.length;
  const n = revLines.length;
  // dp[i][j] = length of LCS of origLines[i..] and revLines[j..]
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array(n + 1).fill(0),
  );
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (origLines[i] === revLines[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  // Walk the table to produce the diff, merging adjacent remove+insert
  // into 'changed' when they are paired (consecutive).
  const raw: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (origLines[i] === revLines[j]) {
      raw.push({ text: origLines[i], tag: 'equal', origLineNum: i, revLineNum: j, page: 0 });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      raw.push({ text: origLines[i], tag: 'removed', origLineNum: i, revLineNum: -1, page: 0 });
      i++;
    } else {
      raw.push({ text: revLines[j], tag: 'added', origLineNum: -1, revLineNum: j, page: 0 });
      j++;
    }
  }
  while (i < m) {
    raw.push({ text: origLines[i], tag: 'removed', origLineNum: i, revLineNum: -1, page: 0 });
    i++;
  }
  while (j < n) {
    raw.push({ text: revLines[j], tag: 'added', origLineNum: -1, revLineNum: j, page: 0 });
    j++;
  }

  // Post-process: merge consecutive removed+added into 'changed'.
  const merged: DiffLine[] = [];
  let k = 0;
  while (k < raw.length) {
    if (raw[k].tag === 'removed') {
      // Collect consecutive removed lines.
      const removedBlock: DiffLine[] = [];
      while (k < raw.length && raw[k].tag === 'removed') {
        removedBlock.push(raw[k]);
        k++;
      }
      // Collect consecutive added lines right after.
      const addedBlock: DiffLine[] = [];
      while (k < raw.length && raw[k].tag === 'added') {
        addedBlock.push(raw[k]);
        k++;
      }
      if (addedBlock.length > 0) {
        // This is a 'changed' block. Emit both sides as 'changed'.
        for (const dl of removedBlock) {
          merged.push({ ...dl, tag: 'changed' });
        }
        for (const dl of addedBlock) {
          merged.push({ ...dl, tag: 'changed' });
        }
      } else {
        // Pure removal.
        merged.push(...removedBlock);
      }
    } else if (raw[k].tag === 'added') {
      // Collect consecutive added (no preceding removed = pure addition).
      const addedBlock: DiffLine[] = [];
      while (k < raw.length && raw[k].tag === 'added') {
        addedBlock.push(raw[k]);
        k++;
      }
      merged.push(...addedBlock);
    } else {
      merged.push(raw[k]);
      k++;
    }
  }

  // Compute page numbers from --- Page N --- markers.
  let currentPage = 1;
  const pageMarkerRe = /^---\s*Page\s+(\d+)\s*---/;
  for (const dl of merged) {
    const match = dl.text.match(pageMarkerRe);
    if (match) {
      currentPage = parseInt(match[1], 10);
    }
    dl.page = currentPage;
  }

  return merged;
}

const DIFF_STYLES: Record<DiffTag, string> = {
  equal: '',
  added: 'bg-green-100 text-green-900',
  removed: 'bg-red-100 text-red-900 line-through',
  changed: 'bg-amber-100 text-amber-900',
};

// Faint spacer background per tag (used on the opposite side so the reader
// sees WHERE text was added/removed without rendering the other side's text).
const SPACER_STYLES: Record<DiffTag, string> = {
  equal: '',
  added: 'bg-green-50',
  removed: 'bg-red-50',
  changed: 'bg-amber-50',
};

const SideBySideViewer = forwardRef<
  SideBySideViewerHandle,
  SideBySideViewerProps
>(function SideBySideViewer(
  { originalFilename, revisionFilename, originalText, revisionText },
  ref,
) {
  const origPaneRef = useRef<HTMLDivElement>(null);
  const revPaneRef = useRef<HTMLDivElement>(null);
  const [flashPage, setFlashPage] = useState<number | null>(null);
  const [flashLineKey, setFlashLineKey] = useState<string | null>(null);

  const diffLines = useMemo(
    () => computeLineDiff(originalText, revisionText),
    [originalText, revisionText],
  );

  // Both panes iterate over the FULL aligned diff sequence so rows line up
  // 1:1. A line that does not belong to a side (origLineNum/revLineNum
  // === -1) renders as a colored spacer on that side.
  const aligned = diffLines;

  // Find the start index of the Nth non-equal block (a maximal run of
  // consecutive diffLines with tag !== 'equal') in the aligned sequence.
  const findBlockStart = (blockIndex: number): number => {
    let count = -1;
    let inBlock = false;
    for (let i = 0; i < aligned.length; i++) {
      const ne = aligned[i].tag !== 'equal';
      if (ne && !inBlock) {
        inBlock = true;
        count++;
        if (count === blockIndex) return i;
      } else if (!ne) {
        inBlock = false;
      }
    }
    return -1;
  };

  const scrollPaneToChild = (
    pane: HTMLDivElement | null,
    idx: number,
    side: string,
  ) => {
    if (!pane) return;
    const child = pane.children[idx] as HTMLElement | undefined;
    if (child) {
      child.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setFlashLineKey(`${side}-${idx}`);
    }
  };

  useImperativeHandle(ref, () => ({
    scrollToPage: (page: number) => {
      setFlashPage(page);
      const markerText = `--- Page ${page} ---`;
      const findChangeIdx = (lines: DiffLine[]) => {
        const pageIdx = lines.findIndex((l) => l.text.trim() === markerText.trim());
        if (pageIdx < 0) return -1;
        for (let i = pageIdx + 1; i < lines.length; i++) {
          const lineText = lines[i].text.trim();
          if (/^---\s*Page\s+\d+\s*---/.test(lineText)) break;
          if (lines[i].tag !== 'equal') return i;
        }
        return pageIdx;
      };
      const idx = findChangeIdx(aligned);
      if (idx >= 0) {
        scrollPaneToChild(origPaneRef.current, idx, 'orig');
        scrollPaneToChild(revPaneRef.current, idx, 'rev');
      }
      setTimeout(() => setFlashPage(null), 2000);
      setTimeout(() => setFlashLineKey(null), 2500);
    },
    scrollToDifferenceBlock: (blockIndex: number) => {
      const idx = findBlockStart(blockIndex);
      if (idx < 0) return;
      scrollPaneToChild(origPaneRef.current, idx, 'orig');
      scrollPaneToChild(revPaneRef.current, idx, 'rev');
      // Flash the page marker for this block too so the reader sees the page.
      setFlashPage(aligned[idx].page || null);
      setTimeout(() => setFlashPage(null), 2000);
      setTimeout(() => setFlashLineKey(null), 2500);
    },
  }));

  // Render a single aligned row for one side. A line that does not belong
  // to this side renders as a colored spacer so the panes stay aligned.
  const renderRow = (dl: DiffLine, idx: number, side: 'orig' | 'rev') => {
    const belongs = side === 'orig' ? dl.origLineNum >= 0 : dl.revLineNum >= 0;
    const isPageMarker = /^---\s*Page\s+\d+\s*---/.test(dl.text.trim());
    const isFlash =
      flashPage !== null &&
      isPageMarker &&
      dl.text.trim() === `--- Page ${flashPage} ---`;
    const isLineFlash = flashLineKey === `${side}-${idx}`;

    if (isPageMarker) {
      return (
        <div
          key={idx}
          className={`text-xs font-semibold text-slate-400 py-1 border-t border-slate-200 mt-2 ${
            isFlash ? 'ring-2 ring-primary-400 rounded animate-pulse' : ''
          }`}
        >
          {dl.text}
        </div>
      );
    }

    if (!belongs) {
      // Spacer: this line exists only on the opposite side. Show a faint
      // colored blank so the reader sees WHERE text was added/removed.
      const spacerCls = SPACER_STYLES[dl.tag];
      return (
        <div
          key={idx}
          className={`text-sm font-mono whitespace-pre-wrap break-words px-1 ${spacerCls} ${
            isLineFlash ? 'ring-2 ring-primary-500 rounded animate-pulse' : ''
          }`}
          aria-hidden="true"
        >
          {'\u00a0'}
        </div>
      );
    }

    const cls = DIFF_STYLES[dl.tag];
    return (
      <div
        key={idx}
        className={`text-sm font-mono whitespace-pre-wrap break-words px-1 ${cls} ${
          isLineFlash ? 'ring-2 ring-primary-500 rounded animate-pulse bg-primary-50' : ''
        }`}
      >
        {dl.text || '\u00a0'}
      </div>
    );
  };

  return (
    <div className="flex gap-2 h-full min-h-0" data-testid="side-by-side-viewer">
      {/* Original (left) */}
      <div className="flex-1 flex flex-col min-w-0 border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 flex-shrink-0">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Original</span>
          <span className="text-sm font-medium text-slate-900 truncate">{originalFilename}</span>
        </div>
        <div
          ref={origPaneRef}
          className="flex-1 overflow-y-auto p-2 space-y-0"
          data-testid="orig-pane"
        >
          {aligned.length === 0 && (
            <div className="text-sm text-slate-400 p-4">No text available.</div>
          )}
          {aligned.map((dl, idx) => renderRow(dl, idx, 'orig'))}
        </div>
      </div>
      {/* Revision (right) */}
      <div className="flex-1 flex flex-col min-w-0 border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 flex-shrink-0">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Revision</span>
          <span className="text-sm font-medium text-slate-900 truncate">{revisionFilename}</span>
        </div>
        <div
          ref={revPaneRef}
          className="flex-1 overflow-y-auto p-2 space-y-0"
          data-testid="rev-pane"
        >
          {aligned.length === 0 && (
            <div className="text-sm text-slate-400 p-4">No text available.</div>
          )}
          {aligned.map((dl, idx) => renderRow(dl, idx, 'rev'))}
        </div>
      </div>
    </div>
  );
});

export default SideBySideViewer;